import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import useBlogStore from '../store/useBlogStore';
import MobileBottomNav from '../components/common/MobileBottomNav';
import { Loader2, Calendar, User, Tag, ArrowLeft } from 'lucide-react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFacebookF, faTwitter, faLinkedin } from "@fortawesome/free-brands-svg-icons";

const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    } catch (e) {
        return "Invalid Date";
    }
};

const BlogPostPage = () => {
    const { slug } = useParams();
    const { post, posts: recentPosts, categories, tags, isLoading, fetchPostBySlug, fetchPosts, fetchCategories, fetchTags } = useBlogStore();

    useEffect(() => {
        window.scrollTo(0, 0);
        if (slug) {
            fetchPostBySlug(slug);
        }
        fetchPosts(1, 5);
        fetchCategories();
        fetchTags();
    }, [slug, fetchPostBySlug, fetchPosts, fetchCategories, fetchTags]);

    if (isLoading && !post) {
        return (
            <div className="flex justify-center items-center h-screen bg-gray-50">
                <Loader2 className="w-12 h-12 animate-spin text-blue-500" />
            </div>
        );
    }

    if (!post) {
        return (
            <div className="text-center py-20 text-gray-500 min-h-screen">
                <h2 className="text-2xl font-semibold">Post not found.</h2>
                <Link to="/blogs" className="mt-4 inline-block text-blue-600 hover:underline">
                    &larr; Back to all posts
                </Link>
            </div>
        );
    }

    const filteredRecentPosts = recentPosts.filter(p => p.id !== post.id).slice(0, 4);

    return (
        <div className="bg-gray-50 min-h-screen">
            <div className="max-w-7xl mx-auto px-4 pt-2 pb-12">
                <Link to="/blogs" className="inline-flex items-center gap-2 text-blue-600 hover:underline mb-2">
                    <ArrowLeft size={16} /> Back to Blog
                </Link>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2">
                        <img
                            src={post.featured_image_url}
                            alt={post.title}
                            className="w-full h-auto max-h-[450px] object-cover rounded-lg shadow-lg mb-6"
                        />
                        <div className="mb-6">
                            <div className="flex items-center text-sm text-gray-500 mb-2 space-x-4">
                                <span className="flex items-center gap-1"><Calendar size={14} /> {formatDate(post.published_at)}</span>
                                <span className="flex items-center gap-1"><User size={14} /> {post.author_name}</span>
                                <span className="flex items-center gap-1"><Tag size={14} /> {post.category_name}</span>
                            </div>
                            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
                                {post.title}
                            </h1>
                        </div>
                        <div className="prose prose-lg max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: post.content }} />
                        <div className="mt-8 pt-6 border-t border-gray-200 flex items-center justify-between">
                            <span className="text-sm font-semibold text-gray-700">Share this post:</span>
                            <div className="flex space-x-2">
                                <a href={`https://twitter.com/intent/tweet?url=${window.location.href}&text=${post.title}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-blue-400 text-white rounded-full flex items-center justify-center hover:bg-blue-500 transition-colors">
                                    <FontAwesomeIcon icon={faTwitter} />
                                </a>
                                <a href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors">
                                    <FontAwesomeIcon icon={faFacebookF} />
                                </a>
                                <a href={`https://www.linkedin.com/shareArticle?mini=true&url=${window.location.href}&title=${post.title}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-blue-700 text-white rounded-full flex items-center justify-center hover:bg-blue-800 transition-colors">
                                    <FontAwesomeIcon icon={faLinkedin} />
                                </a>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-1">
                        <div className="sticky top-8 space-y-8">
                            {filteredRecentPosts.length > 0 && (
                                <div className="bg-white rounded-lg shadow-md p-6">
                                    <h3 className="text-xl font-bold text-gray-900 mb-4">Recent Posts</h3>
                                    <div className="space-y-4">
                                        {filteredRecentPosts.map(p => (
                                            <Link key={p.id} to={`/blog/${p.slug}`} className="flex items-center gap-4 group">
                                                <img src={p.featured_image_url} alt={p.title} className="w-16 h-16 object-cover rounded-md flex-shrink-0" />
                                                <div>
                                                    <p className="text-xs text-gray-500">{formatDate(p.published_at)}</p>
                                                    <h4 className="text-sm font-semibold text-gray-800 group-hover:text-blue-600 transition-colors">{p.title}</h4>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {categories.length > 0 && (
                                <div className="bg-white rounded-lg shadow-md p-6">
                                    <h3 className="text-xl font-bold text-gray-900 mb-4">Categories</h3>
                                    <ul className="space-y-2">
                                        {categories.map(cat => (
                                            <li key={cat.id}>
                                                <Link to="#" className="text-gray-600 hover:text-blue-600 transition-colors">{cat.name}</Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {tags.length > 0 && (
                                <div className="bg-white rounded-lg shadow-md p-6">
                                    <h3 className="text-xl font-bold text-gray-900 mb-4">Tags</h3>
                                    <div className="flex flex-wrap gap-2">
                                        {tags.map(tag => (
                                            <Link key={tag.id} to="#" className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full hover:bg-blue-100 hover:text-blue-700 transition-colors">
                                                {tag.name}
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <MobileBottomNav />
        </div>
    );
};

export default BlogPostPage;